<template>
  <div class="page-container">
    <div class="page-header">
      <div class="d-flex align-items-center w-100">
        <b-button variant="link" class="mr-3" @click="onBack()">
          <v-icon name="buttons/chevrons_left" :size="13"/>
        </b-button>
        <h4 v-if="isBusy" class="m-0 mr-auto" id="task-ellipsis-loading">Загрузка заявки на смену</h4>
        <h4 v-else class="m-0">{{title}}</h4>
        <span v-if="!isBusy" :class="['custom-tag ml-auto mr-3', getStatusStyle(newItem.status_id)]">
          {{`Статус: ${getStatusName(newItem.status_id)}`}}
        </span>
        <b-button id="save-button" class="custom-btn-primary" :disabled="isDisabled || loading" @click="handleSubmit()">
          <b-spinner v-if="loading" small/>
          <v-icon name="buttons/check" :size="13" class="ml-1"/>
          Сохранить
        </b-button>
        <b-tooltip v-if="hasAnyMismatch" target="save-button" :key="'save-total-' + hasAnyMismatch" class="tag-warning">
          Внимание: итого некоторых исполнителей не совпадает с суммой их услуг.
        </b-tooltip>
      </div>
    </div>

    <div class="page-body" style="overflow-y: auto;">
      <b-skeleton-wrapper :loading="isBusy">
        <template #loading>
          <b-row>
            <b-col cols="6">
              <b-tabs card class="custom-tabs">
                <b-tab disabled>
                  <template #title>
                    <v-icon name="buttons/user_file" :size="13" class="mr-2"/>
                    <span>Основная информация</span>
                  </template>
                </b-tab>
                <b-tab disabled>
                  <template #title>
                    <v-icon name="buttons/payment" :size="13" class="mr-2"/>
                    <span>Расходы</span>
                  </template>
                </b-tab>
                <b-tab disabled>
                  <template #title>
                    <v-icon name="buttons/file" :size="13" class="mr-2"/>
                    <span>Документы</span>
                  </template>
                </b-tab>
              </b-tabs>
              <b-card class="custom-card m-3 px-1 pt-2">
                <b-row>
                  <b-col cols="6">
                    <b-skeleton width="17%" height="20%" class="mb-1"/>
                    <b-skeleton type="input" width="100%" height="43%"/>
                  </b-col>
                  <b-col cols="6">
                    <b-skeleton width="13%" height="20%" class=""/>
                    <b-skeleton type="input" width="100%" height="43%"/>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <b-skeleton width="24%" height="20%" class=""/>
                    <b-skeleton type="input" width="100%" height="25%"/>
                  </b-col>
                  <b-col>
                    <b-skeleton width="29%" height="20%" class=""/>
                    <b-skeleton type="input" width="100%" height="25%"/>
                  </b-col>
                </b-row>
              </b-card>
              <b-card class="custom-card mb-2">
                <b-row class="pt-1">
                  <b-col>
                    <b-skeleton width="60%" height="24%" class="mb-2"/>
                    <b-skeleton type="input" width="100%" height="44%"/>
                  </b-col>
                  <b-col>
                    <b-skeleton width="66%" height="24%" class="mb-2"/>
                    <b-skeleton type="input" width="100%" height="44%"/>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <b-skeleton width="75%" height="24%" class="mb-2"/>
                    <b-skeleton type="input" width="100%" height="44%"/>
                  </b-col>
                  <b-col>
                    <b-skeleton width="82%" height="24%" class="mb-2"/>
                    <b-skeleton type="input" width="100%" height="44%"/>
                  </b-col>
                </b-row>
                <b-row class="mt-1 mb-4">
                  <b-col>
                    <b-skeleton type="input" width="42%" height="78%"/>
                  </b-col>
                </b-row>
              </b-card>
              <b-card class="custom-card mb-2">
                <b-row>
                  <b-col>
                    <b-skeleton width="20%" height="24%" class="mb-2"/>
                    <b-skeleton type="input" width="100%" height="43%"/>
                  </b-col>
                </b-row>
                <b-row class="mb-2">
                  <b-col>
                    <b-skeleton width="42%" height="24%" class="mb-2"/>
                    <b-skeleton type="input" width="100%" height="45%"/>
                  </b-col>
                </b-row>
                <b-row class="pb-4">
                  <b-col>
                    <b-skeleton width="14%" height="24%" class="mb-2"/>
                    <b-skeleton type="input" width="100%" height="77%"/>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>
            <b-col cols="6">
              <b-card class="custom-card">
                <b-skeleton width="13%" height="50%" class="pt-3"/>
                <b-list-group flush bg-variant="light">
                  <b-list-group-item
                    v-for="i in 11"
                    :key="'skeleton-item-' + i"
                    bg-variant="light"
                    class="d-flex justify-content-between align-items-center"
                  >
                    <!-- Left -->
                    <div class="flex-grow-1">
                      <b-skeleton :width="getLeftSkeletonWidth(i)" height="20%" class="mb-0"/>
                      <span v-if="i === 7 || i === 9 || i === 11">
                            <b-skeleton type="text" width="45%" class="mt-2"/>
                          </span>
                    </div>
                    <!-- Right -->
                    <div>
                      <b-skeleton :width="getRightSkeletonWidth(i)" class="mr-5"/>
                    </div>
                  </b-list-group-item>
                </b-list-group>
              </b-card>
            </b-col>
          </b-row>

          <div class="page-body">
            <b-row>
              <b-col align-v="center" cols="auto">
                <h5 class="text-muted">Исполнители</h5>
              </b-col>
              <b-col cols="auto" class="mr-auto">
                <b-skeleton type="input" width="520%" height="68%"/>
              </b-col>
              <b-col cols="auto">
                <b-skeleton type="input" width="260%" height="68%"/>
              </b-col>
              <b-col cols="auto" class="ml-5">
                <b-skeleton type="input" width="110%" height="68%"/>
              </b-col>
              <b-col cols="auto" class="mr-5">
                <b-skeleton type="input" width="250%" height="68%"/>
              </b-col>
            </b-row>
            <div class="text-center text-muted d-flex align-items-center justify-content-center">
              <div class="spinner">
                <div class="rect1"></div>
                <div class="rect2"></div>
                <div class="rect3"></div>
                <div class="rect4"></div>
                <div class="rect5"></div>
              </div>
              <h5>Загрузка исполнителей</h5>
            </div>
          </div>
        </template>
      </b-skeleton-wrapper>

      <div v-show="!isBusy">
        <b-row>
          <b-col cols="6">
            <b-tabs card class="custom-tabs">
              <b-tab active>
                <template #title>
                  <v-icon name="buttons/user_file" :size="13" class="mr-2"/>
                  <span>Основная информация</span>
                </template>
                <b-card class="custom-card mb-2">
                  <b-row>
                    <b-col cols="6">
                      <b-form-group class="mb-0" id="group-location" label="Объект">
                        <location-select
                          id="input-location-select"
                          ref="location_select"
                          :value="newItem.location_id"
                          :is-disabled="isDisabled"
                          :state="getFieldState('location_id')"
                          @onSelect="onLocationChange"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group class="mb-0" id="group-start-address" :label="$t('message.tasks.modal.start_address')">
                        <b-form-input
                          id="input_start_address"
                          size="sm"
                          type="text"
                          :disabled="isDisabled"
                          :state="getFieldState('start_address')"
                          :placeholder="$t('message.tasks.modal.start_addressPlaceholder')"
                          v-model="newItem.start_address"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col cols="6">
                      <b-form-group id="group-executor" label="Менеджер">
                        <user-select
                          id="input-user-select"
                          class="mr-2 flex-fill"
                          ref="user_select"
                          :state="getFieldState('executor')"
                          :value="newItem.executor"
                          :is-disabled="isDisabled || newItem.location_id === 0 || userSelectIsBusy"
                          @onSelect="onSelectExecutor"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group id="group-project" label="Руководитель">
                        <b-form-input
                          id="input-project"
                          readonly
                          :value="newItem.project_name || '-'"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col cols="6">
                      <b-form-group id="group-task-group" label="Группа">
                        <task-group-select
                          :value="newItem.group_id"
                          :is-disabled="isDisabled"
                          @onSelect="onSelectTaskGroup"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-card>

                <b-card class="custom-card mb-2">
                  <b-row>
                    <b-col>
                      <b-form-group id="group-startDate" :label="$t('message.tasks.modal.start_date')">
                        <b-form-input
                          id="input_startDate"
                          size="sm"
                          type="date"
                          :disabled="isDisabled"
                          :placeholder="$t('message.tasks.modal.start_datePlaceholder')"
                          v-model.date="startDate"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col>
                      <b-form-group id="group-startTime" :label="$t('message.tasks.modal.start_time')">
                        <b-form-input
                          id="input_startTime"
                          size="sm"
                          type="time"
                          :disabled="isDisabled"
                          v-model.time="startTime"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <b-form-group id="group-endDate" :label="$t('message.tasks.modal.end_date')">
                        <b-form-input
                          id="input-endDate"
                          size="sm"
                          type="date"
                          :disabled="isDisabled"
                          v-model.date="endDate"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col>
                      <b-form-group id="group-endTime" :label="$t('message.tasks.modal.end_time')">
                        <b-form-input
                          id="input-endTime"
                          size="sm"
                          type="time"
                          :disabled="isDisabled"
                          v-model.time="endTime"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-button class="custom-btn-primary" :disabled="isDisabled" @click.stop="setWorkersDateTapped()">
                    <v-icon name="buttons/card_check" :size="15" class="ml-1"/>
                    {{"Установить дату и время исполнителям"}}
                  </b-button>
                </b-card>

                <b-card class="custom-card">
                  <b-row>
                    <b-col>
                      <b-form-group id="group-work-type" label="Вид работ объекта">
                        <location-works-select
                          id="location-works-select"
                          ref="location_works_select"
                          :is-disabled="isDisabled"
                          :state="getFieldState('task_location_works')"
                          @onSelect="onLocationWorkSelect"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <b-form-group id="group-workers-count" label="Планируемое количество исполнителей">
                        <b-form-input
                          id="input-workers-count"
                          size="sm"
                          type="number"
                          min="0"
                          :disabled="isPlanWorkersCountDisabled()"
                          :state="getFieldState('plan_workers_count')"
                          v-model.number="newItem.plan_workers_count"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-form-group id="group-cargo-description" label="Примечание">
                    <b-form-textarea
                      id="textarea-cargo-description"
                      size="sm"
                      :disabled="isDisabled"
                      v-model="newItem.cargo_description"
                      :rows="2"
                      :max-rows="2"
                      no-resize
                    />
                  </b-form-group>
                </b-card>
              </b-tab>

              <b-tab>
                <template #title>
                  <v-icon name="buttons/payment" :size="13" class="mr-2"/>
                  <span>Расходы</span>
                </template>
                <b-card class="custom-card">
                  <b-row>
                    <b-col cols="6">
                      <b-form-group id="group_additional_cost" :label="$t('message.tasks.modal.additional_cost')">
                        <b-form-input
                          id="input_additional_cost"
                          size="sm"
                          type="number"
                          :disabled="isDisabled"
                          v-model.number="newItem.additional_cost"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col>
                      <b-form-group id="group_additional_income" :label="$t('message.tasks.modal.additional_income')">
                        <b-form-input
                          id="input_additional_income"
                          size="sm"
                          type="number"
                          :disabled="isDisabled"
                          v-model.number="newItem.additional_income"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col>
                      <b-form-group id="group_declarated_cost" :label="$t('message.tasks.modal.declarated_cost')">
                        <b-form-input
                          id="input_declarated_cost"
                          size="sm"
                          type="number"
                          :disabled="isDisabled"
                          v-model.number="newItem.declarated_cost"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-form-group id="group_description" :label="$t('message.tasks.modal.description')">
                    <b-form-textarea
                      id="textarea_description"
                      size="sm"
                      :disabled="isDisabled"
                      :placeholder="$t('message.tasks.modal.descriptionPlaceholder')"
                      v-model="newItem.description"
                      :rows="2"
                      :max-rows="2"
                      no-resize
                    />
                  </b-form-group>
                </b-card>
              </b-tab>

              <b-tab>
                <template #title>
                  <v-icon name="buttons/file" :size="13" class="mr-2"/>
                  <span>Документы</span>
                </template>
                <task-files ref="task_files" :is-disabled="isDisabled"/>
              </b-tab>
            </b-tabs>
          </b-col>
          <b-col cols="6">
            <b-card class="custom-card">
              <h5 id="task-header">Итоговые расчёты</h5>
              <b-list-group flush bg-variant="light">
                <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                  Заказчик
                  <strong>{{ customer.name }}</strong>
                </b-list-group-item>
                <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                  Объект
                  <strong>{{ location.name }}</strong>
                </b-list-group-item>
                <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                  Стоимость за единицу
                  <strong>{{ newItem.payment_first }}</strong>
                </b-list-group-item>
                <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                  {{ getTotalTitle() }}
                  <strong>{{priceFormat(newItem.total_hours)}}</strong>
                </b-list-group-item>
                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  Сумма с заказчика
                  <strong>{{priceFormat(newItem.total_price)}}</strong>
                </b-list-group-item>
                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  Исполнителям
                  <strong>{{priceFormat(newItem.total_worker_price)}}</strong>
                </b-list-group-item>
                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    Исполнителям
                    <br>
                    <span class="text-small text-secondary">
                      с учетом коммиссии сервиса <strong>{{ newItem.smz_cost }}%</strong>
                    </span>
                  </div>
                  <strong>{{priceFormat(newItem.total_worker_price_with_comiss)}}</strong>
                </b-list-group-item>
                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  {{$t('message.tasks.modal.total_delta')}}
                  <strong>{{priceFormat(newItem.total_delta)}}</strong>
                </b-list-group-item>
                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    Маржинальность
                    <br>
                    <span class="text-small text-secondary">без учета расходов за месяц</span>
                  </div>
                  <strong>{{ marginality }} %</strong>
                </b-list-group-item>
                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  Менеджеру
                  <strong>{{priceFormat(newItem.total_manager)}}</strong>
                </b-list-group-item>
                <b-list-group-item v-if="visibleProject()" class="d-flex justify-content-between align-items-center">
                  <div>
                    Руководителю
                    <br>
                    <span class="text-small text-secondary">без учета расходов за месяц</span>
                  </div>
                  <strong>{{ newItem.project_id != null ? priceFormat(newItem.total_project) : "-" }}</strong>
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </b-col>
        </b-row>

        <header class="page-header">
          <div class="d-flex align-items-center w-100">
            <h5 class="mb-0">Исполнители</h5>
            <b-button id="allWorkerPriceButton" ref="allWorkerPriceButton" class="custom-btn-secondary ml-3 mr-auto">
              Назначить
            </b-button>
            <!--<b-button v-if="visibleAdmins()" size="sm mx-2" variant="warning" :disabled="isDisabled" @click="setPayAllWorkers">-->
            <!--Оплатить всем-->
            <!--</b-button>-->
            <export-sidebar
              sidebar-id="sidebar-task-workers-export"
              storage-key="sidebar_task_workers_export"
              :columns="exportableFields"
              :data-to-export="dataToExport"
              :data-mapper="mapTaskForExport"
              file-name="TaskWorkers.xlsx"
              sheet-name="TaskWorkers"
              :is-busy="isBusy"
              :items-count="totalTWRows"
              class="mr-1"
            />
            <b-button id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle.sidebar-fields @click="onPopoverClose">
              <v-icon name="buttons/cog" :size="13"/>
            </b-button>
            <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
              Колонки
            </b-tooltip>
            <b-button class="custom-btn-primary" :disabled="isDisabled" @click="showFreeWorkers">
              <v-icon name="buttons/plus" :size="12" class="ml-1"/>
              Добавить исполнителя
            </b-button>
          </div>

          <b-popover
            target="allWorkerPriceButton"
            triggers="click"
            :show.sync="popoverShow"
            placement="auto"
            container="my-container"
            ref="popover"
            @show="onShow"
            @shown="onShown"
            @hidden="onHidden"
          >
            <template #title>
              <div class="d-flex justify-content-between align-items-center w-100">
                <small class="font-weight-bold text-muted">Назначить исполнителям</small>
                <b-button @click="onPopoverClose" class="close pb-1" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </b-button>
              </div>
            </template>

            <div>
              <b-form-group label="Ставка" label-for="allWorkerPriceInput" label-cols="3" class="mb-1">
                <b-form-input
                  ref="allWorkerPriceInput"
                  id="allWorkerPriceInput"
                  type="number"
                  placeholder="Введите ставку"
                  v-model.number="allWorkerPriceModel"
                  size="sm"
                />
              </b-form-group>
              <b-form-group label="Обед" label-cols="3">
                <b-form-input
                  ref="allWorkerDinnerInput"
                  type="number"
                  placeholder="Введите обед"
                  v-model.number="allWorkerDinnerModel"
                  size="sm"
                />
              </b-form-group>
              <hr/>
              <b-form-group label="Услуги СМЗ" label-cols="3">
                <b-row>
                  <b-col cols="auto">
                    <b-form-group label="Услуга" class="mb-1">
                      <b-form-select
                        v-model="allSmzWorkerServiceSelected"
                        :options="locationWorkServices"
                        size="sm"
                      >
                        <template #first>
                          <b-form-select-option :value="null" disabled>Выберите услугу</b-form-select-option>
                        </template>
                      </b-form-select>
                    </b-form-group>
                  </b-col>
                  <b-col>
                    <b-form-group label="Количество" class="mb-0">
                      <b-form-input
                        ref="allSmzWorkerServiceQuantityInput"
                        type="number"
                        placeholder="Введите количество"
                        v-model.number="allSmzWorkerServiceQuantity"
                        size="sm"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-form-group>
              <div class="d-flex justify-content-end mt-1">
                <b-button @click="onPopoverClose" class="custom-btn-secondary">Закрыть</b-button>
                <b-button @click="onPopoverOk" class="custom-btn-primary ml-1">Назначить</b-button>
              </div>
            </div>
          </b-popover>
        </header>

        <div class="page-body">
          <template v-if="isBusy || (newItem.task_workers && newItem.task_workers.length > 0)">
            <div class="page-table-container">
              <b-table
                id="workers"
                ref="TaskWorkers"
                class="mb-0 text-small custom-table"
                :tbody-tr-class="tbodyRowClass"
                select-mode="multi"
                responsive
                :items="newItem.task_workers"
                :fields="renderedFields"
                :current-page="currentPage"
                :per-page="perPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                @row-dblclicked="onRowDblClicked"
                @row-clicked="onRowClicked"
              >
                <template #head(selected)="row">
                  <b-form-checkbox
                    class="mx-1"
                    v-model="allSelected"
                    :indeterminate="indeterminate"
                    size="md"
                    @change="selectPageRows"
                  />
                </template>

                <template #cell(selected)="row">
                  <b-form-checkbox
                    class="mx-1"
                    size="md"
                    :checked="row.item.selected"
                    @click.native.stop
                  />
                </template>

                <template #cell(name)="row">
                  {{row.item.name}}
                  <br/>
                  <div class="m-0 p-0 text-secondary text-small" v-if="row.item.inn != null">ИНН: {{row.item.inn}}</div>
                  <a :href=telUrl(row.item.phone) class="m-0 p-0 text-small">{{phoneString(row.item.phone)}}</a>
                </template>

                <template #cell(price)="row">
                  <b-form-input
                    id="input_price1"
                    type="number"
                    :min="0"
                    :disabled="isDisabled"
                    size="sm"
                    autocomplete="off"
                    v-model.number="row.item.price"
                    @keydown="preventMinus"
                  />
                </template>

                <template #cell(start_date)="row">
                  <b-form inline>
                    <b-form-input
                      id="input_startDate1"
                      type="date"
                      :disabled="isDisabled"
                      class="mr-1"
                      size="sm"
                      v-model.date="row.item.string_start_date"
                    />
                    <b-form-input
                      id="input_startTime1"
                      type="time"
                      :disabled="isDisabled"
                      class=""
                      size="sm"
                      v-model.date="row.item.start_time"
                    />
                  </b-form>
                </template>

                <template #cell(end_date)="row">
                  <b-form inline>
                    <b-form-input
                      id="input_endDate1"
                      type="date"
                      :disabled="isDisabled"
                      class="mr-1"
                      size="sm"
                      v-model.date="row.item.string_end_date"
                    />
                    <b-form-input
                      id="input_endTime1"
                      type="time"
                      :disabled="isDisabled"
                      class=""
                      size="sm"
                      v-model.date="row.item.end_time"
                    />
                  </b-form>
                </template>

                <template #cell(road_price)="row">
                  <b-form-input
                    id="input_price"
                    type="number"
                    :disabled="isDisabled"
                    size="sm"
                    autocomplete="off"
                    v-model.number="row.item.road_price"
                  />
                </template>

                <template #cell(dinner)="row">
                  <b-form-input
                    id="input_dinner"
                    type="number"
                    :disabled="isDisabled"
                    step="0.5"
                    size="sm"
                    autocomplete="off"
                    v-model.number="row.item.dinner"
                  />
                </template>

                <template #cell(prepaid)="row">
                  <b-form-input
                    id="input_prepaid"
                    type="number"
                    :disabled="isDisabled"
                    size="sm"
                    autocomplete="off"
                    v-model.number="row.item.prepaid"
                  />
                </template>

                <template #cell(total_units)="row">
                  <div v-if="newItem.unit_code === '539'">
                    {{ row.item.total_units }}
                  </div>
                  <div v-else>
                    <b-form-input
                      id="input_total_units"
                      type="number"
                      :disabled="isDisabled"
                      size="sm"
                      autocomplete="off"
                      v-model.number="row.item.total_units"
                    />
                  </div>
                </template>

                <template #cell(total_price)="row">
                  <div
                    :id="'total-price-' + row.item.worker_id"
                    :class="{'text-danger font-weight-bold': mismatchingTotals.includes(row.item.worker_id)}"
                    v-b-tooltip="{
                    title: mismatchingTotals.includes(row.item.worker_id) ? getTooltipTitle(row.item) : '',
                    variant: 'danger',
                    placement: 'auto',
                    triggers: 'hover'
                    }"
                  >
                    {{ priceFormat(row.item.total_price) }}
                  </div>
                </template>

                <template #cell(description)="row">
                  <b-form-textarea
                    id="textarea_descriptionq"
                    :disabled="isDisabled"
                    :placeholder="$t('message.tasks.modal.descriptionPlaceholder')"
                    v-model="row.item.description"
                    :rows="2"
                    :max-rows="2"
                    size="sm"
                  />
                </template>

                <template #cell(paid)="row">
                  <div v-if="visibleAdmins()">
                    <button
                      type="button"
                      :class="['custom-tag', getPaidStyle(row)]"
                      :disabled="isDisabled || newItem.status_id === 1"
                      @click.stop="showModalAccept(row)"
                    >
                      {{getPaidTitle(row)}}
                    </button>

                    <b-button
                      v-if="row.item.paid && row.item.has_integration_details"
                      size="sm"
                      variant="info"
                      :disabled="isDisabled"
                      @click="onPaidDetails(row)"
                    >
                      <font-awesome-icon icon="info"/>
                    </b-button>
                  </div>

                  <div v-else>
                    {{getPaidTitle(row)}}
                    <b-button
                      v-if="row.item.paid"
                      size="sm"
                      variant="info"
                      :disabled="isDisabled"
                      @click="onPaidDetails(row)"
                    >
                      <font-awesome-icon icon="info"/>
                    </b-button>
                  </div>
                </template>

                <template #cell(action)="row">
                  <div class="d-flex align-items-center" style="gap: 8px;">
                    <b-button id="tws-details" :class="row.detailsShowing ? 'custom-btn-secondary' : 'custom-btn-primary'" @click="row.toggleDetails()">
                      <v-icon name="buttons/page_list" :size="14"/>
                    </b-button>
                    <b-button class="custom-btn-danger" :disabled="isDisabled" @click="onRemove(row)">
                      <v-icon name="buttons/trash" :size="14"/>
                    </b-button>
                  </div>
                </template>

                <template #row-details="row">
                  <b-card no-body class="m-0 p-2">
                    <task-worker-services
                      ref="task_worker_services"
                      :tws="row.item.task_worker_services"
                      :tw="row.item"
                      :tws-options="locationWorkServices"
                      :disabled="isDisabled"
                      @change="row.item.task_worker_services = $event"
                    />
                  </b-card>
                </template>
              </b-table>
            </div>

            <div class="page-pagination">
              <pagination-footer
                :total-rows="totalTWRows"
                :per-page="perPage"
                :current-page.sync="currentPage"
                @per-page-change="onPerPageChange"
              />
            </div>
          </template>

          <div v-else class="empty-state-container">
            <v-icon name="navbar/empty_table" :size="90"/>
            <h5>Нет добавленных исполнителей</h5>
          </div>
        </div>

        <workers-free
          ref="free_workers_table"
          :workers="newItem.task_workers"
          @addToTask="addToTask"
        />

        <b-modal
          id="modal-accept"
          ref="modalAccept"
          :title="$t('message.tasks.modal.acceptWorkerTitle')"
          @ok="acceptItem()"
          :ok-title="$t('message.actions.ok')"
          :cancel-title="$t('message.actions.cancel')"
          centered
        >
          <p>{{ $t("message.tasks.modal.acceptWorkerDesc") }} "{{acceptWorker.name}}"</p>
        </b-modal>

        <b-modal id="modal-tw-details" ref="modalTWDetails" title="Детали оплаты" ok-only size="lg">
          <div v-if="twDetails != null">
            <b-list-group flush bg-variant="light">
              <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                Задание
                <strong>{{ this.twDetails.name }}</strong>
              </b-list-group-item>
              <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                Описание
                <strong>{{ this.twDetails.description }}</strong>
              </b-list-group-item>
              <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                Стоимость
                <strong>{{ priceFormat(this.twDetails.price) }} ₽</strong>
              </b-list-group-item>
              <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                Статус
                <strong>{{ this.twDetails.status }}</strong>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                Дата создания
                <strong>{{ this.twDetails.created_at }}</strong>
              </b-list-group-item>
              <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                Договор
                <b-link :href="this.twDetails.contract.url" target="_blank">{{ this.twDetails.contract.name }}</b-link>
              </b-list-group-item>
              <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                Акт
                <b-link :href="this.twDetails.act.url" target="_blank">{{ this.twDetails.act.name }}</b-link>
              </b-list-group-item>
              <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                Чек
                <b-link :href="this.twDetails.act.receipt.link" target="_blank">Открыть</b-link>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                Статус оплаты
                <strong>{{ this.twDetails.act.status_pay }}</strong>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                Оплачено исполнителю
                <strong>{{ priceFormat(this.twDetails.act.sum) }} ₽</strong>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                Удержанный налог
                <strong>{{ priceFormat(this.twDetails.act.tax) }} ₽</strong>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                Комиссия сервиса
                <strong>{{ priceFormat(this.twDetails.act.commission) }} ₽</strong>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                Дата оплаты
                <strong>{{ formatTableDateTime(new Date(this.twDetails.act.receipt.created_time)) }}</strong>
              </b-list-group-item>
            </b-list-group>
          </div>
        </b-modal>
      </div>

      <dynamic-tables
        storage-key="taskWorkers_columns"
        sidebar-fields="sidebar-fields"
        :fields-model.sync="fieldsTW"
        fieldKey="name"
      />
    </div>
  </div>
</template>

<script>
import tasks from "../../network/tasks";
import catalogs from "../../network/catalogs";
import users from "../../network/users";
import customers from "../../network/customers";
import locations from "../../network/locations";
import balansPro from "../../network/balansPro";
  import dateFormatter from '../../utilites/dateFormatter'
  import helpers from "../../utilites/helpers";
  import unitsStore from "../../utilites/unitStore";
  import permissions, {PERM} from "../../utilites/permissions";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import ExportSidebar from "../../utilites/commonUi/ExportSidebar.vue";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import BaseSelect from "../../utilites/commonUi/BaseSelect.vue";
    import taskService from "./taskService";
    import WorkersFree from "../Workers/WorkersFree";
    import UnitSelect from "../Catalogs/UnitSelect";
    import LocationSelect from "../Locations/LocationSelect";
    import UserSelect from "../Users/UserSelect";
    import TaskFiles from "./TaskFiles";
    import LocationWorksSelect from "../Locations/LocationWorksSelect";
    import TaskWorkerServices from "./TaskWorkerServices.vue";
    import TaskGroupSelect from "../Catalogs/TaskGroups/TaskGroupSelect.vue";

export default {
  name: 'Task',
  components: {
    TaskGroupSelect, BaseSelect, PaginationFooter, ExportSidebar, TaskWorkerServices, DynamicTables, TaskFiles, WorkersFree, UnitSelect, LocationSelect, UserSelect, LocationWorksSelect
  },
  mixins: [
    tasks, catalogs, users, customers, locations, balansPro,
    dateFormatter, helpers, unitsStore, taskService, permissions
  ],
  data() { return this.defaultData(); },
  async created() {
    this.user = (await this.getUserInfo()).data;
    this.statuses = (await this.getTaskStatuses()).data.items;

    const taskId = this.$route.params.task_id;

    if (taskId) {
      this.title = "Заявка на смену № "+ taskId;
      await this.getTask(taskId);
    } else {
      this.author = this.user;
      this.executor = this.user;
      this.updateExecutorRate();
    }

    if (this.$route.meta.isCopy) {
      this.title = "Копирование заявки на смену";
    }

    this.stopLoadingView();
    this.stopLoadingData();
  },
  async mounted() {
    if (this.$refs.location_select) {
      await this.$refs.location_select.configure(this.newItem.location_id);
    }
    if (this.$refs.user_select) {
      await this.$refs.user_select.configure(this.newItem.executor, this.newItem.location_id, 2);
    }
  },
  computed: {
    totalTWRows() {
      return this.newItem.task_workers.length;
    },
    marginality() {
      const delta = this.newItem.total_delta;
      const profit = this.newItem.total_price;

      if (profit === 0) return 0;

      return Math.round((delta / profit) * 10000) / 100;
    },
    visibleFields() {
      return this.fieldsTW.filter(field => field.visible);
    },
    renderedFields() {
      return [
        this.serviceStartField,
        ...this.visibleFields,
        this.serviceEndField
      ];
    },
    dataToExport() {
      return this.selected.length > 0 ? this.selected : this.newItem.task_workers;
    },
    exportableFields() {
      return [...this.fieldsTW, ...this.additionalExportFields]
        .filter(field => field.exportable)
        .map(field => ({
          key: field.key,
          label: field.label,
          visible: field.visible,
        }));
    },
    hasAnyMismatch() {
      return this.mismatchingTotals.length > 0;
    }
  },
  watch: {
    // Порядок отслежвания важен
    'newItem.unit_code': {
      handler(newVal, oldVal) {
        // Только для Человеко-часов
        if (newVal !== oldVal && newVal === '539') {
          this.newItem.task_workers.forEach(tw => {
            tw.total_units = this.getTotalHours(tw);
          });
        }
      }, deep: true
    },
    'newItem.task_workers': {
      handler(newValues) {
        if (!newValues) return;

        const mismatchedIds = [];

        newValues.forEach((tw) => {
          if (tw.start_time && tw.end_time) {
            tw.start_date = this.formatServerDateTime(tw.string_start_date, tw.start_time);
            tw.end_date = this.formatServerDateTime(tw.string_end_date, tw.end_time);

            // Только для Человеко-часов
            if (this.newItem.unit_code === '539') {
              const diff = this.getTotalHours(tw);
              tw.total_units = diff <= 0 ? 0 : diff;
            }
          }

          const hasTWS = tw.task_worker_services && tw.task_worker_services.length > 0;
          const totalPrice = this.calculateTotalWorkerPrice(tw);

          tw.total_price = totalPrice;

          if (hasTWS) {
            const twsTotal = tw.task_worker_services.reduce((acc, service) => {
              const price = Number(service.price) || 0;
              const quantity = Number(service.quantity) || 0;
              const calculatedTotal = price * quantity;
              
              if (!('total_price' in service)) {
                this.$set(service, 'total_price', calculatedTotal);
              }

              return acc + (service.total_price !== undefined ? service.total_price : calculatedTotal);
            }, 0);

            if (Math.abs(totalPrice - twsTotal) > 0.01) {
              mismatchedIds.push(tw.worker_id);
            }
          }
        });

        this.mismatchingTotals = mismatchedIds;
      }, deep: true, immediate: true
    },
    'newItem': {
      handler() {
        this.updateTotals();
        this.updateTotalDelta();
      }, deep: true
    },
    'startDate': {
      async handler() {
        this.updateTotals();
        this.updateTotalDelta();
      }, deep: true
    },
    'startTime': {
      handler() {
        this.updateTotals();
      }, deep: true
    },
    'endDate': {
      handler() {
        this.updateTotals();
      }, deep: true
    },
    'endTime': {
      handler() {
        this.updateTotals();
      }, deep: true
    },
    selected(newValue) {
      if (newValue.length === 0) {
        this.indeterminate = false;
        this.allSelected = false;
      } else if (newValue.length === this.newItem.task_workers.length) {
        this.indeterminate = false;
        this.allSelected = true;
      } else {
        this.indeterminate = true;
        this.allSelected = false;
      }
    },
  },
  methods: {
    defaultData() {
      return {
        title: "Добавление заявки на смену",
        startDate: this.formatDate(new Date()),
        startTime: this.formatTime(new Date()),
        endDate: this.formatDate(new Date()),
        endTime: this.formatTime(new Date()),
        user: '',
        author: '',
        executor: { id: 0, name: '' },
        customer: { id: 0, name: '' },
        location: { id: 0, name: '' },
        newItem: {
          id: null,
          start_address: '',
          end_address: '',
          start_date: '',
          end_date: '',
          payment_type_int: 0,
          payment_first_count: 0,
          payment_first: 250,
          payment_second_count: 0,
          payment_second: 0,
          workers_count: 0,
          total_hours: 0,
          total_price: 0,
          total_worker_price: 0,
          total_worker_price_with_comiss: 0,
          total_delta: 0,
          total_manager: 0,
          additional_cost: 0,
          additional_income: 0,
          description: '',
          cargo_description: '',
          customer_id: 0,
          customer_name: '',
          author: 0,
          executor: 0,
          executor_name: '',
          paid: false,
          contact: '',
          declarated_cost: 0,
          task_workers: [],
          customer: {},
          location_id: 0,
          location_name: '',
          read_only: false,
          executor_rate: 0,
          town_id: null,
          work_type_id: 1,
          unit_code: "539",
          plan_workers_count: 0,
          status_id: 1,
          management_cost: 0,
          smz_cost: 0,
          project_id: null,
          project_rate: null,
          total_project: null,
          project_name: '',
          invoice_id: null,
          invoice_numb: null,
          task_location_works: [],
          profit_tax: 0,
          nds: 0,
          transfer_commission: 0,
          bank_commission: 0,
          ip_commission: 0,
          group_id: null,
        },
        isDisabled: false,
        serviceStartField: {
          key: 'selected',
          label: '',
          class:'twSelect'
        },
        fieldsTW: [
          {
            key: 'name',
            label: 'Исполнитель',
            class: 'align-middle twName',
            sortable: true,
            visible: true,
            exportable: true,
          },
          {
            key: 'price',
            label: this.$t('message.workers.table.price'),
            class: 'align-middle twPrice',
            sortable: true,
            visible: true,
            exportable: true,
          },
          {
            key: 'start_date',
            label: 'Дата и время начала',
            class: 'align-middle twDateTime',
            visible: true,
            exportable: true,
          },
          {
            key: 'end_date',
            label: 'Дата и время окончания',
            tdClass: 'align-middle twDateTime',
            visible: true,
            exportable: true,
          },
          {
            key: 'dinner',
            label: this.$t('message.workers.table.dinner'),
            class: 'align-middle twDinner',
            visible: true,
            exportable: true,
          },
          {
            key: 'total_units',
            label: 'Кол-во',
            class: 'align-middle twTotalUnits',
            sortable: true,
            visible: true,
            exportable: true,
          },
          {
            key: 'prepaid',
            label: this.$t('message.workers.table.prepaid'),
            class: 'align-middle twPrepaid',
            visible: true,
            exportable: true,
          },
          {
            key: 'road_price',
            label: this.$t('message.workers.table.road_price'),
            class: 'align-middle twRoadPrice',
            visible: true,
            exportable: true,
          },
          {
            key: 'total_price',
            label: this.$t('message.workers.table.total_price'),
            class: 'align-middle twTotalPrice',
            sortable: true,
            visible: true,
            exportable: true,
          },
          {
            key: 'description',
            label: this.$t('message.workers.table.description'),
            class: 'twDescription',
            visible: true,
            exportable: true,
          },
          {
            key: 'paid',
            label: this.$t('message.workers.table.paid'),
            class: 'align-middle twPaid',
            sortable: true,
            visible: true,
          },
        ],
        serviceEndField: {
          key: 'action',
          label: this.$t('message.workers.table.actions'),
          class: 'align-middle twAction',
        },
        additionalExportFields: [
          {
            key: 'phone',
            label: "Телефон",
            visible: true,
            exportable: true,
          },
          {
            key: 'inn',
            label: "ИНН",
            visible: true,
            exportable: true,
          },
        ],
        sortBy: 'name',
        sortDesc: false,
        currentPage: 1,
        perPage: 100,
        isBusy: true,
        loading: true,
        acceptWorker: { worker_id: null, name: '' },
        readOnlyButtonVisible: false,
        allWorkerPriceModel: null,
        allWorkerDinnerModel: null,
        allSmzWorkerServiceSelected: null,
        allSmzWorkerServiceQuantity: null,
        popoverShow: false,
        statuses: [],
        leftSkeletonWidth: {  1: '21%', 2: '17%', 3: '50%', 4: '40%', 5: '43%', 6: '34%', 7: '34%', 8: '15%', 9: '41%', 10: '27%', 11: '33%' },
        rightSkeletonWidth: {  1: '100%', 2: '100%', 3: '50%', 4: '50%', 5: '100%', 6: '100%', 7: '100%', 8: '100%', 9: '100%', 10: '100%', 11: '100%' },
        twDetails: null,
        locationWorkServices: [],
        mismatchingTotals: [],
        selected: [],
        allSelected: false,
        indeterminate: false,
        userSelectIsBusy: false
      }
    },
    onBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push('/tasks').catch(() => {});
      }
    },
    stopLoadingView() {
      setTimeout(() => { this.isBusy = false; }, 500);
    },
    startLoadingData() {
      this.loading = true;
    },
    stopLoadingData() {
      setTimeout(() => { this.loading = false; }, 500);
    },
    async authorInfo(userId) {
      this.author = (await this.getUserById(userId)).data;
    },
    async executorInfo(userId) {
      this.executor = (await this.getUserById(userId)).data;
      this.updateTotalDelta();
    },
    clearNewItem() {
      Object.assign(this.$data, this.defaultData());
    },
    async setNewItem(item) {
      const localItem = { ...item };

      if (localItem.executor > 0) {
        await this.executorInfo(localItem.executor);
      }

      localItem.task_workers = (localItem.task_workers || []).map(twItem => {
        const taskWorker = this.mapWorkerToTaskWorker(twItem);

        if (this.$route.meta.isCopy) {
          taskWorker.paid = false;
          taskWorker.status_pay = 'unpaid';
        }
        return taskWorker;
      });

      // Если необходимо копировать заявку. Сбрасываем статус, факт оплаты, read_only = false.
      if (this.$route.meta.isCopy) {
        localItem.read_only = false;
        localItem.author = this.user.id;
        localItem.status_id = 1;
        localItem.paid = false;
        localItem.invoice_id = null;
        localItem.invoice_numb = null;
        await this.authorInfo(localItem.author);
      }

      this.newItem = localItem;

      this.$nextTick(async () => {
        // Устанавливаем Исполнителя
        if (this.$refs.user_select) {
          await this.$refs.user_select.configure(this.newItem.executor, this.newItem.location_id, 2);
        }
        // Устанавливаем объект
        if (this.$refs.location_select) {
          await this.$refs.location_select.configure(this.newItem.location_id);
        }
        // Конфигурируем файлы
        if (this.$refs.task_files) {
          await this.$refs.task_files.configure(this.newItem.id);
        }
      });

      this.prepareReadOnly();

      this.startDate = this.formatDate(this.getDateFromServerString(item.start_date));
      this.startTime = this.formatTime(this.getDateFromServerString(item.start_date));
      this.endDate = this.formatDate(this.getDateFromServerString(item.end_date));
      this.endTime = this.formatTime(this.getDateFromServerString(item.end_date));

      if (this.newItem.location_id > 0) {
        await this.onLocationChange(this.newItem.location_id, true);
      } else if (this.newItem.customer_id > 0) {
      } else if (this.newItem.customer_id > 0) {
        // Старые заявки у которых нет объекта
        await this.getCustomer(this.newItem.customer_id);
      }
    },
    prepareReadOnly() {
      if (this.$route.name === 'TasksView') {
        this.isDisabled = true;
        this.readOnlyButtonVisible = false;
        return;
      }

      if (this.hasPermission([PERM.TASK_EDIT_OTHER])) {
        this.readOnlyButtonVisible = true;
        this.isDisabled = false;
        return;
      }

      if (this.newItem.read_only) {
        this.isDisabled = true;
        this.readOnlyButtonVisible = false;
        return;
      }

      if (this.hasPermission([PERM.TASK_EDIT])) {
        this.readOnlyButtonVisible = false;
        this.isDisabled = false;
      }
    },
    getFieldState(field) {
      const value = this.newItem[field];

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (typeof value === 'number') {
        return value > 0;
      }

      return !!value;
    },
    mapWorkerToTaskWorker(item) {
      const fromServerST = this.getDateFromServerString(item.start_date);
      const fromServerET = this.getDateFromServerString(item.end_date);

      const stDT = this.formatDateTime(fromServerST);
      const etDT = this.formatDateTime(fromServerET);

      return {
        id: item.id,
        worker_id: item.worker_id,
        name: item.worker_name,
        phone: item.worker_phone,
        inn: item.worker.inn,
        price: item.price,
        road_price: item.road_price,
        start_time: this.formatTime(fromServerST),
        end_time: this.formatTime(fromServerET),
        total_price: item.total_price,
        prepaid: item.prepaid,
        dinner: item.dinner,
        description: item.description,
        start_date: this.formatServerDate(this.getDateToServer(stDT)),
        end_date: this.formatServerDate(this.getDateToServer(etDT)),
        string_start_date: this.formatDate(fromServerST),
        string_end_date: this.formatDate(fromServerET),
        paid: item.paid,
        status_pay: 'unpaid',
        worker: item.worker,
        total_units: item.total_units,
        task_worker_services: item.task_worker_services,
        has_integration_details: item.has_integration_details
      };
    },
    async getTask(task_id) {
      const taskResult = await this.getTaskById(task_id);

      if (taskResult.data) {
        await this.setNewItem(taskResult.data);
      } else {
        this.clearNewItem();
      }
    },
    // Проверки на нулевые значения в number
    parseNulls() {
      this.newItem.payment_first_count = this.newItem.payment_first_count || 0;
      this.newItem.payment_first = this.newItem.payment_first || 0;
      this.newItem.payment_second_count = this.newItem.payment_second_count || 0;
      this.newItem.additional_cost = this.newItem.additional_cost || 0;
      this.newItem.additional_income = this.newItem.additional_income || 0;
      this.newItem.declarated_cost = this.newItem.declarated_cost || 0;
      this.newItem.plan_workers_count = this.newItem.plan_workers_count || 0;

      this.newItem.task_workers.forEach(item => {
        item.price = item.price || 0.0;
        item.road_price = item.road_price || 0.0;
        item.total_price = item.total_price || 0.0;
        item.prepaid = item.prepaid || 0.0;
        item.dinner = item.dinner || 0.0;
      });
    },
    async handleSubmit() {
      if (this.newItem.location_id === 0) {
        this.showSubmitError("Выберите объект.");
        return;
      }

      if (!this.newItem.start_address) {
        this.showSubmitError("Заполните адрес заявки на смену.");
        return;
      }

      if (this.newItem.executor === 0) {
        this.showSubmitError("Выберите менеджера.");
        return;
      }

      if (this.newItem.task_location_works.length === 0) {
        this.showSubmitError("Выберите вид работ объекта.");
        return;
      }

      if (!this.newItem.unit_code) {
        this.showSubmitError("Выберите единицы измерения.");
        return;
      }

      if (this.newItem.plan_workers_count === 0) {
        this.showSubmitError("Планируемое количество исполнителей должно быть больше 0.");
        return;
      }

      const startDateTime = new Date(`${this.startDate}T${this.startTime}`);
      const endDateTime = new Date(`${this.endDate}T${this.endTime}`);

      if (endDateTime < startDateTime) {
        this.showSubmitError("Время окончания заявки должно быть позже времени начала.");
        return;
      }

      if (this.newItem.task_workers) {
        for (const worker of this.newItem.task_workers) {
          if (new Date(worker.end_date) < new Date(worker.start_date)) {
            this.showSubmitError("Проверьте даты исполнителей. Время окончания должно быть позже времени начала.");
            return;
          }
        }
      }

      if (this.newItem.author === 0) {
        this.newItem.author = this.user.id;
      }

      this.newItem.location_name = this.location.name;
      this.newItem.executor = this.executor.id;
      this.newItem.executor_name = this.executor.name;

      this.newItem.start_date = this.formatServerDateTime(this.startDate, this.startTime);
      this.newItem.end_date = this.formatServerDateTime(this.endDate, this.endTime);

      const projectUser = this.location.location_users.find(item => item.location_position_id === 1);

      if (projectUser != null) {
        this.newItem.project_id = projectUser.user_id;
        this.newItem.project_rate = projectUser.rate;
      } else {
        this.newItem.project_id = null;
        this.newItem.project_rate = null;
      }

      this.parseNulls();

      const data = this.newItem;
      const isUpdate = this.newItem.id > 0 && !this.$route.meta.isCopy;
      const method = isUpdate ? this.putTask : this.postTask;

      if (this.$route.meta.isCopy) {
        data.id = null;
      }

      this.startLoadingData();
      const result = await method(data);
      this.stopLoadingData();

      if (!result || !result.data) {
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.newItem.id = result.data.id;
      this.$emit('refresh');
      this.onBack();
    },
    // Итого
    getTotalTitle() {
      const unit = this.getUnitByCode(this.newItem.unit_code);
      if (!unit) return "Количество";
      if (!unit.shortName) return "Количество";

      return "Количество" + " " + unit.shortName;
    },
    updateTotals() {
      // Итого рабочим
      let workersPrice = this.calculateWorkersPrice(this.newItem.task_workers);

      if (workersPrice !== this.newItem.total_worker_price) {
        this.newItem.total_worker_price = workersPrice;
      }

      // Прибавляем расход на вывод через сервисы
      if ( this.newItem.smz_cost > 0) {
        const serviceCoast = this.newItem.smz_cost / 100;
        workersPrice = workersPrice + (workersPrice * serviceCoast);
      }

      if (workersPrice !== this.newItem.total_worker_price_with_comiss) {
        this.newItem.total_worker_price_with_comiss = workersPrice;
      }

      // Итого часов
      const totalHours = this.calculateWorkersHours(this.newItem.task_workers);
      if (totalHours !== this.newItem.total_hours) {
        this.newItem.total_hours = totalHours;
      }

      // Итого с заказчика
      const totalPrice = this.calculateTotalPrice(this.newItem);
      if (totalPrice !== this.newItem.total_price) {
        this.newItem.total_price = totalPrice;
      }
    },
    updateTotalDelta() {
      const totalDelta = this.calculateTotalDelta(this.newItem);
      if (totalDelta !== this.newItem.total_delta) {
        this.newItem.total_delta = totalDelta;
      }

      this.updateTotalManager();
      this.updateTotalProject();
    },
    updateTotalManager() {
      if (!this.location) {
        if (this.newItem.total_manager !== 0) {
          this.newItem.total_manager = 0;
        }
        return;
      }

      switch (this.location.rate_type) {
        case 1: { // В процентах
          const totalManager1 = this.calculateTotalDeltaWithRatePercent(this.newItem.total_delta, this.newItem.executor_rate);
          if (totalManager1 !== this.newItem.total_manager) this.newItem.total_manager = totalManager1;
          break;
        }
        case 2: { // В рублях
          const totalManager2 = this.calculateTotalDeltaWithRateUnits(this.newItem.total_hours, this.newItem.executor_rate);
          if (totalManager2 !== this.newItem.total_manager) this.newItem.total_manager = totalManager2;
          break;
        }
        default: {
          const totalManagerDef = this.calculateTotalDeltaWithRatePercent(this.newItem.total_delta, this.newItem.executor_rate);
          if (totalManagerDef !== this.newItem.total_manager) this.newItem.total_manager = totalManagerDef;
        }
      }
    },
    updateTotalProject() {
      if (this.newItem.project_id) {
        const deltaForProject = this.newItem.total_delta - this.newItem.total_manager;
        switch (this.location.rate_type) {
          case 1: { // В процентах
            const totalProjectPercent = deltaForProject * this.newItem.project_rate / 100;
            this.newItem.total_project = Math.round(totalProjectPercent * 100) / 100;
            break;
          }
          case 2: { // В рублях
            const totalProjectUnit = this.newItem.total_hours * this.newItem.project_rate;
            this.newItem.total_project = Math.round(totalProjectUnit * 100) / 100;
            break;
          }
        }
      }
    },
    updateExecutorRate() {
      const executorRate = this.getExecutorRate();
      if (executorRate !== this.newItem.executor_rate) {
        this.newItem.executor_rate = executorRate;
      }
    },
    getExecutorRate() {
      if (this.executor == null) { return 0; }
      if (this.executor.id === 0) { return 0; }
      if (this.location == null) { return 0; }
      if (this.location.location_users == null) { return 0; }

      const executorId = this.executor.id;
      const locationUser = this.location.location_users.find(x => x.user_id === executorId && x.location_position_id === 2);
      if (locationUser == null) { return 0; }
      return locationUser.rate;
    },
    // Действия
    getCustomerPaidTitle(paid) {
      return paid ? "Оплачено заказчиком" : "Не оплачено заказчиком";
    },
    getCustomerPaidVariant(paid) {
      return paid ? "success" : "warning";
    },
    paidTapped() {
      this.newItem.paid = !this.newItem.paid;
    },
    // Status
    getStatusName(status_id) {
      const status = this.statuses.find(e => e.id === status_id)
      return status != null ? status.name : 'Ошибка';
    },
    getStatusStyle(status_id) {
      switch (status_id) {
        case 1:
          return 'tag-info';
        case 2:
          return 'tag-primary';
        case 3:
          return 'tag-secondary';
        case 4:
          return 'tag-success';
        default:
          return '';
      }
    },
    setWorkersDateTapped() {
      this.newItem.task_workers.forEach(twItem => {
        const st = this.startTime ? this.startTime : twItem.start_time;
        const et = this.endTime ? this.endTime : twItem.end_time;

        twItem.start_time = st;
        twItem.end_time = et;
        twItem.start_date = this.formatServerDateTime(this.startDate, st);
        twItem.end_date = this.formatServerDateTime(this.endDate, et);
        twItem.string_start_date = this.startDate;
        twItem.string_end_date = this.endDate;
      });
    },
    setPayAllWorkers() {
      this.newItem.task_workers.forEach(twItem => {
        twItem.paid = true;
      });
    },
    // Закачики
    updateCustomer(customer) {
      this.customer = customer;
      this.newItem.customer_id = customer.id;
      this.newItem.customer_name = customer.name;
    },
    async getCustomer(customer_id) {
      const customerResult = await this.getCustomerById(customer_id);

      if (!customerResult.data) {
        this.removeCustomer();
        return;
      }

      this.updateCustomer(customerResult.data);
    },
    removeCustomer() {
      this.customer = { id: 0, name: '' };
      this.newItem.customer_id = 0;
      this.newItem.customer_name = '';
    },
    // Объект
    async onLocationChange(locationId, isInit = false) {
      if (locationId === this.location.id) return;

      if (locationId === 0) {
        await this.clearLocation();
        return;
      }

      this.userSelectIsBusy = true;

      const locationResult = await this.getLocationById(locationId);

      if (!locationResult) {
        this.userSelectIsBusy = false;
        return;
      }

      this.userSelectIsBusy = false;
      this.location = locationResult.data;

      this.newItem.location_id = this.location.id;
      this.newItem.town_id = this.location.town_id;
      this.newItem.payment_type_int = this.location.payment_type;
      this.newItem.management_cost = this.location.management_cost;
      this.newItem.smz_cost = this.location.smz_cost;
      this.newItem.profit_tax = this.location.profit_tax;
      this.newItem.nds = this.location.nds;
      this.newItem.transfer_commission = this.location.transfer_commission;
      this.newItem.bank_commission = this.location.bank_commission;
      this.newItem.ip_commission = this.location.ip_commission;

      if (!this.newItem.start_address) {
        this.newItem.start_address = locationResult.data.address;
      }

      if (this.location.location_work_services) {
        const data = this.location.location_work_services;
        this.locationWorkServices = data.map(service => ({
          value: service.id,
          text: service.work_service_name,
          details: service
        }));
      }

      const projectUser = this.location.location_users.find(item => item.location_position_id === 1);

      if (projectUser) {
        this.newItem.project_id = projectUser.user_id;
        this.newItem.project_rate = projectUser.rate;

        const projectUserResult = await this.getUserById(projectUser.user_id);

        if (projectUserResult.data) {
          this.newItem.project_name = projectUserResult.data.name;
        }
      } else {
        this.newItem.project_id = null;
        this.newItem.project_rate = null;
        this.newItem.project_name = '';
      }

      this.updateExecutorRate();

      await this.getCustomer(this.location.customer_id);

      const managerUser = this.location.location_users.find(item => item.location_position_id === 2);

      if (isInit !== true) {
        if (managerUser) {
          this.newItem.executor = managerUser.user_id;
          this.newItem.executor_name = '';
        } else {
          this.newItem.executor = 0;
          this.newItem.executor_name = '';
        }
      }

      if (this.$refs.user_select) {
        await this.$refs.user_select.configure(this.newItem.executor, this.newItem.location_id, 2);
      }

      if (this.$refs.location_works_select) {
        const locationWorks = this.location.locations_work_types;
        let currentLocationWorkId = 0;

        if (this.newItem.id > 0 && this.newItem.task_location_works.length > 0) {
          currentLocationWorkId = this.newItem.task_location_works[0].location_work_id;
        }
        await this.$refs.location_works_select.configure(currentLocationWorkId, locationWorks);
      }
    },
    async clearLocation() {
      this.location = { id: 0, name: '' };
      this.executor = { id: 0, name: '' };
      this.removeCustomer();
      this.newItem.location_id = 0;
      this.newItem.town_id = null;
      this.newItem.payment_type_int = 0;
      this.newItem.payment_first = 0;
      this.newItem.start_address = '';
      this.newItem.executor = 0;
      this.newItem.executor_name = '';
      this.newItem.project_id = 0;
      this.newItem.project_name = '';
      this.newItem.group_id = null;

      if (this.$refs.user_select) {
        await this.$refs.user_select.configure(this.newItem.executor, this.newItem.location_id, 2);
      }

      this.newItem.task_location_works = [];

      if (this.$refs.location_works_select) {
        await this.$refs.location_works_select.configure(0, []);
      }
    },
    onLocationWorkSelect(locationWork) {
      if (!locationWork) {
        if (this.newItem.task_location_works.length === 1) {
          this.newItem.task_location_works.splice(0, 1);
        }
        this.newItem.payment_first = 0;
        this.newItem.unit_code = "539";
        return;
      }

      if (this.newItem.task_location_works.length === 1) {
        this.newItem.task_location_works.splice(0, 1);
      }

      this.newItem.task_location_works.push({
        task_id: this.newItem.id,
        location_id: this.location.id,
        location_work_id: locationWork.id,
        work_type_id: locationWork.work_type_id,
        work_type_name: locationWork.work_type_name,
        rate:locationWork.rate,
        unit_code:locationWork.unit_code
      });

      this.newItem.payment_first = locationWork.rate;
      this.newItem.unit_code = locationWork.unit_code;
      this.newItem.work_type_id = locationWork.work_type_id;
    },
    async onSelectExecutor(userId) {
      if (userId === 0) {
        await this.clearExecutor();
        return
      }

      const userResult = await this.getUserById(userId);
      if (!userResult.data) return;

      this.executor = userResult.data;
      this.newItem.executor = this.executor.id;
      this.updateExecutorRate();
    },
    async clearExecutor() {
      this.executor = { id: 0, name: '' }
      this.newItem.executor = 0;
    },
    onSelectTaskGroup(group) {
      if (group) {
        this.$set(this.newItem, 'group_id', group.id);
      } else {
        this.$set(this.newItem, 'group_id', null);
      }
    },
    //Task Workers Methods
    showFreeWorkers() {
      if (this.$refs.free_workers_table) {
        this.onPopoverClose();
        this.$refs.free_workers_table.showModalSelect();
      }
    },
    getDateTime(date, time) {
      const st = time ? time : this.formatTime(new Date());
      return this.formatServerDateTime(date, st);
    },
    getStartDateTime() {
      return this.getDateTime(this.startDate, this.startTime);
    },
    getEndDateTime() {
      return this.getDateTime(this.endDate, this.endTime);
    },
    existWorker(id) {
      return this.newItem.task_workers.find(x => x.worker_id === id) !== undefined;
    },
    onRemove(row) {
      if (row.item) {
        this.deleteRow = row.item
      }
      const title = this.$t('message.workers.modal.removeTitle');
      const message = `${this.$t("message.workers.modal.removeDesc")} ${this.deleteRow.worker.name}?`;

      this.showModalDelete(title, message, () => {
        const index = this.newItem.task_workers.indexOf(row.item);
        this.newItem.task_workers.splice(index, 1);
      });
    },
    getTotalHours(tw) {
      let diff = ((this.getDateToServer(tw.end_date) - this.getDateToServer(tw.start_date)) / 60000 / 60);
      diff = Math.round(diff * 100) / 100;
      // Вычитаем обед
      return diff - tw.dinner;
    },
    addToTask(workers) {
      workers.forEach(worker => {
        if (!this.existWorker(worker.id)) {
          const st = this.startTime ? this.startTime : this.formatTime(new Date());
          const et = this.endTime ? this.endTime : this.formatTime(new Date());

          const taskWorker = {
            worker_id: worker.id,
            name: worker.name,
            phone: worker.phone,
            inn: worker.inn,
            price: 0.0,
            road_price: 0.0,
            start_time: st,
            end_time: et,
            total_price: 0.0,
            prepaid: 0.0,
            dinner: 0.0,
            description: '',
            start_date: this.formatServerDateTime(this.startDate, st),
            end_date: this.formatServerDateTime(this.endDate, et),
            string_start_date: this.startDate,
            string_end_date: this.endDate,
            paid: false,
            status_pay: 'unpaid',
            worker: worker,
            total_units: 0,
            task_worker_services: []
          };
          // Только для Человеко-часов
          if (this.newItem.unit_code === '539') {
            taskWorker.total_units = this.getTotalHours(taskWorker);
          }

          this.newItem.task_workers.push(taskWorker);
        }
      });
    },
    acceptItem() {
      this.acceptWorker.paid = !this.acceptWorker.paid;
      this.newItem.task_workers.find(x => x.worker_id === this.acceptWorker.worker_id).paid = this.acceptWorker.paid;

      if (this.acceptWorker.paid) {
        this.newItem.task_workers.find(x => x.worker_id === this.acceptWorker.worker_id).status_pay = 'paid';
      } else {
        this.newItem.task_workers.find(x => x.worker_id === this.acceptWorker.worker_id).status_pay = 'unpaid';
      }
      this.hideModalAccept();
    },
    showModalAccept(row) {
      if (this.hasPermission([PERM.TASK_EDIT_OTHER])) {
        this.acceptWorker = row.item;
        this.acceptWorker.paid = !this.acceptWorker.paid;
        this.newItem.task_workers.find(x => x.worker_id === this.acceptWorker.worker_id).paid = this.acceptWorker.paid;

        if (this.acceptWorker.paid) {
          this.newItem.task_workers.find(x => x.worker_id === this.acceptWorker.worker_id).status_pay = 'paid';
        } else {
          this.newItem.task_workers.find(x => x.worker_id === this.acceptWorker.worker_id).status_pay = 'unpaid';
        }
        return;
      }

      if (this.hasPermission([PERM.TASK_EDIT])) {
        if (!row.item.paid) {
          this.acceptWorker = row.item;
          this.$refs.modalAccept.show();
        }
      }
    },
    hideModalAccept() {
      this.$refs.modalAccept.hide();
    },
    getPaidStyle(row) {
      return row.item.paid ? 'tag-success' : 'tag-danger';
    },
    getPaidTitle(row) {
      return row.item.paid ? 'Оплачено' : 'Не оплачено';
    },
    setTaskWorkersItem(option, value) {
      this.newItem.task_workers.forEach(item => {
        item[option] = value;
      });
    },
    addServiceToSmzWorkers(serviceId, quantity) {
      const selectedOption = this.locationWorkServices.find(opt => opt.value === serviceId);

      if (!selectedOption || !selectedOption.details) return;

      const serviceDetails = selectedOption.details;
      const qty = Number(quantity);

      this.newItem.task_workers.forEach(tw => {
        if (tw.worker && tw.worker.employment_type === 1) {
          tw.task_worker_services.push({
            id: 0,
            location_id: this.newItem.location_id,
            location_work_id: 0,
            location_work_service_id: serviceDetails.id,
            location_work_service_name: serviceDetails.work_service_name,
            price: serviceDetails.price,
            quantity: qty,
            total_price: serviceDetails.price * qty,
            unit_code: serviceDetails.unit_code,
          });
        }
      });

      this.$bvToast.toast('Услуги добавлены всем СМЗ исполнителям.', { title: 'Успех', variant: 'success', autoHideDelay: 3000 });
    },
    // Popover workers
    onPopoverClose() {
      this.popoverShow = false;
    },
    onPopoverOk() {
      if (this.allWorkerPriceModel) {
        this.setTaskWorkersItem('price', this.allWorkerPriceModel);
      }

      if (this.allWorkerDinnerModel) {
        this.setTaskWorkersItem('dinner', this.allWorkerDinnerModel);
      }

      if (this.allSmzWorkerServiceSelected) {
        const qty = (this.allSmzWorkerServiceQuantity === null || this.allSmzWorkerServiceQuantity === '')
          ? 0
          : this.allSmzWorkerServiceQuantity;

        this.addServiceToSmzWorkers(this.allSmzWorkerServiceSelected, qty);
      }
      this.onPopoverClose();
    },
    onShow() {
      this.allWorkerPriceModel = null;
      this.allWorkerDinnerModel = null;
      this.allSmzWorkerServiceSelected = null;
      this.allSmzWorkerServiceQuantity = null;
    },
    onShown() {
      if (this.$refs.allWorkerPriceInput) {
        this.focusRef(this.$refs.allWorkerPriceInput);
      }
    },
    onHidden() {
      if (this.$refs.allWorkerPriceButton) {
        this.focusRef(this.$refs.allWorkerPriceButton);
      }
    },
    focusRef(ref) {
      this.$nextTick(() => {
        this.$nextTick(() => {
          (ref.$el || ref).focus();
        })
      })
    },
    isPlanWorkersCountDisabled() {
      // Разрешено для новых заявок
      if (this.newItem.id == null) return false;
      // Разрешено для заявок из копии
      if (this.$route.meta.isCopy) return false;
      // Запрещено для заявок в режиме просмотра
      if (this.isDisabled) return true;
      // Разрешено для администратора
      if (!this.hasPermission([PERM.TASK_EDIT_OTHER])) return true;
      // Запрещено в остальных случаях
      return false;
    },
    userSelectDescription() {
      if (this.newItem.location_id === 0) {
        return "Выберите объект";
      }
      // описание селекта менеджера
      return "Сотрудники у которых есть доступ к выбранному объекту";
    },
    async showAcceptChangeStatus(message) {
      return await this.showAcceptModal("Изменение статуса", message);
    },
    visibleAdmins() {
      return this.hasPermission([PERM.TASK_EDIT_OTHER]);
    },
    visibleProject() {
      return this.hasPermission([PERM.TASK_EDIT_LOCATION]);
    },
    // Export
    mapTaskForExport(item) {
      return {
        name: item ? item.name : '',
        phone: item ? item.phone : '',
        inn: (item && item.worker) ? item.worker.inn : '',
        price: item ? item.price : '',
        start_date: item ? (item.start_date ? this.formatTableDateTime(item.start_date) : '') : '',
        end_date: item ? (item.end_date ? this.formatTableDateTime(item.end_date): '') : '',
        dinner: item ? item.dinner : '',
        total_units: item ? item.total_units : '',
        prepaid: item ? item.prepaid : '',
        road_price: item ? item.road_price : '',
        total_price: item ? item.total_price : '',
        description: item ? item.description : '',
      };
    },
    async selectPageRows(checked) {
      this.selected = checked ? [...this.newItem.task_workers] : [];
      this.updateSelected();
    },
    onRowClicked(item) {
      // Ищем уже выбранные
      const existItem = this.selected.findIndex(e => e.id === item.id);

      if (existItem !== -1) {
        this.selected.splice(existItem, 1);
      } else {
        this.selected.push(item);
      }
      this.updateSelected();
    },
    onRowDblClicked(item) {
      if (Object.prototype.hasOwnProperty.call(item, '_showDetails')) {
        item._showDetails = !item._showDetails;
      } else {
        this.$set(item, '_showDetails', true);
      }
    },
    updateSelected() {
      this.newItem.task_workers.forEach((item) => {
        const isSelected = this.selected.some(e => e.id === item.id);
        this.$set(item, 'selected', isSelected);
      });
    },
    tbodyRowClass(item) {
      return item && item.selected ? ["b-table-row-selected", "table-primary", "pointer-cell"] : ["pointer-cell"];
    },
    // Skeleton
    getLeftSkeletonWidth(i) {
      return this.leftSkeletonWidth[i];
    },
    getRightSkeletonWidth(i) {
      return this.rightSkeletonWidth[i];
    },
    // Детали оплаты исполнителям
    async onPaidDetails(row) {
      const result = await this.getTaskWorkerDetails(row.item.id);

      if (!result.data) return;

      this.twDetails = result.data;

      if (this.twDetails !== null) {
        this.$refs.modalTWDetails.show();
      }
    },
    calculateTWSSum(tw) {
      if (tw.task_worker_services.length === 0) return 0;
      return tw.task_worker_services.reduce((sum, service) => {
        return sum + (service.price || 0) * (service.quantity || 0);
      }, 0);
    },
    getTooltipTitle(tw) {
      const sum = this.calculateTWSSum(tw);
      return `Сумма услуг исполнителя (${this.priceFormat(sum)}) не соответствует итогу.`;
    },
    // Time display
    formatDecimalToTime(decimalValue) {
      if (!decimalValue || isNaN(decimalValue) || decimalValue < 0) return "00:00";
      const hours = Math.floor(decimalValue);
      const minutes = Math.round((decimalValue - hours) * 60);
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    },
    toHHMM(value) {
      const val = parseFloat(value);

      if (!val || isNaN(val)) return "00:00";

      const isNegative = val < 0;
      const total = Math.round(Math.abs(val) * 60);
      const h = Math.floor(total / 60);
      const m = total % 60;
      const sign = isNegative ? "-" : "";
      return `${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    },
    fromHHMM(str) {
      if (!str || typeof str !== 'string') return 0;

      const cleanStr = String(str).trim();
      const isNegative = cleanStr.startsWith('-');
      const timeStr = isNegative ? cleanStr.slice(1) : cleanStr;

      const [h, m] = timeStr.split(':').map(Number);
      const val = Math.round(((h || 0) + (m || 0) / 60) * 100) / 100;
      return isNegative ? -val : val;
    },
    stepDinner(item, deltaMin) {
      let total = Math.round((item.dinner || 0) * 60) + deltaMin;
      if (total < 0) total = 0;
      if (total > 12 * 60) total = 12 * 60;
      item.dinner = Math.round((total / 60) * 100) / 100;
    },
    onPerPageChange(newPerPage) {
      this.perPage = Number(newPerPage);
      this.currentPage = 1;
    },
  },
}
</script>

<style>
@media screen
and (min-width: 768px) {
  a[href*="tel:"] {
    pointer-events: none;
    cursor: default;
    text-decoration: none;
  }
}

/*TW*/
.twName {
  padding-left: 10px !important;
  min-width: 200px;
  max-width: 300px;
}

.twPrice {
  min-width: 120px;
  max-width: 120px;
}

.twDateTime {
  min-width: 220px;
  max-width: 220px;
}

.twDinner {
  min-width: 100px;
  max-width: 100px;
}

.twPrepaid {
  min-width: 100px;
  max-width: 100px;
}

.twTotalUnits {
  text-align: center;
  min-width: 90px;
  max-width: 90px;
}

.twRoadPrice {
  min-width: 100px;
  max-width: 100px;
}

.twTotalPrice {
  text-align: center;
  min-width: 90px;
  max-width: 90px;
}

.twDescription {
  min-width: 100px;
  max-width: 200px;
}

.twPaid {
  text-align: center;
  min-width: 145px;
}

.twAction {
  min-width: 80px;
  padding-right: 10px !important;
  text-align: center;
}

#workers th {
  vertical-align: middle;
}

.top-fixed-thead {
  background-color: lightgray;
}

.no-underline {
  text-decoration: none !important;
}

/* Skeleton table spinner */
.spinner {
  margin: 1%;
  width: 50px;
  height: 40px;
  text-align: center;
  font-size: 10px;
}

.spinner > div {
  background-color: #e0e0e0;
  height: 100%;
  width: 6px;
  display: inline-block;

  -webkit-animation: sk-stretchdelay 1.4s infinite ease-in-out;
  animation: sk-stretchdelay 1.4s infinite ease-in-out;
}

.spinner .rect2 {
  -webkit-animation-delay: -1.1s;
  animation-delay: -1.1s;
}

.spinner .rect3 {
  -webkit-animation-delay: -1.0s;
  animation-delay: -1.0s;
}

.spinner .rect4 {
  -webkit-animation-delay: -0.9s;
  animation-delay: -0.9s;
}

.spinner .rect5 {
  -webkit-animation-delay: -0.8s;
  animation-delay: -0.8s;
}

@-webkit-keyframes sk-stretchdelay {
  0%, 40%, 100% { -webkit-transform: scaleY(0.4) }
  20% { -webkit-transform: scaleY(1.0) }
}

@keyframes sk-stretchdelay {
  0%, 40%, 100% {
    transform: scaleY(0.4);
    -webkit-transform: scaleY(0.4);
  }  20% {
       transform: scaleY(1.0);
       -webkit-transform: scaleY(1.0);
     }
}

#task-header {
  margin-bottom: 0 !important;
  margin-top: 0.5rem !important;
}

#task-ellipsis-loading {
  width: 185px;
  white-space: nowrap;
  display: inline-block;
}

@keyframes blink {
  0% { content: "."; }
  33% { content: ".."; }
  66% { content: "..."; }
  100% { content: "."; }
}

#task-ellipsis-loading::after {
  content: ".";
  animation: blink 1.5s infinite step-start;
}

.close {
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.twSelect {
  min-width: 50px;
  text-align: center;
}

.dinner-number-input {
  width: 78px !important;
  text-align: center;
}

/*
.dinner-time-field {
  position: relative;
  display: inline-block;
  width: 78px;
}

.dinner-time-input {
  padding-right: 18px;
  text-align: center;
}
.dinner-time-arrows {
  position: absolute;
  top: 0;
  right: 1px;
  height: 100%;
  width: 16px;
  display: flex;
  flex-direction: column;
}
.dinner-time-arrows button {
  flex: 1;
  border: none;
  background: #f8f9fa;
  padding: 0;
  font-size: 8px;
  line-height: 1;
  color: #495057;
  cursor: pointer;
}
.dinner-time-arrows button:hover { background: #e9ecef; }
.dinner-time-arrows button:disabled { opacity: .5; cursor: default; }
 */
</style>



// WEBPACK FOOTER //
// src/components/Tasks/Task.vue